// src/encounter/panel-section.tsx
//
// One collapsible section of the encounter's right panel (Antonin, 15 Oct: "right panel of the
// encounter is unusable. make it clean collapsible sections. Check proto for inspiration").
//
// The pattern is the prototype's collapsible section (its home inbox categories and the chart's
// supplement groups): a header row that opens and closes the section, a count beside the title,
// hairlines between the sections, the body simply present or absent (no height animation). What
// the rules add over the prototype: the title in the section role, a Lucide chevron at 2 px that
// points down while open and right while closed, actions as 32 px neutral icon buttons that the
// app-wide instant tooltip names (src/app/icon-button-tooltips.tsx), and `aria-expanded` on the
// one toggle. The look lives in the bridge's `/* encounter right panel */` block (hooks:
// `data-slot="panel-section*"`), on `--enc-right-*` tokens.
//
// OPEN STATE. Local UI state, not remembered: the prototype keeps its sections in component state
// too. Until the clinician touches a section it follows `defaultOpen`, which the host derives from
// the content, so a section whose feed is still loading opens by itself once rows arrive and a
// section that came back empty stays closed. After the first click the clinician's choice wins.
//
// The body is mounted only while open, so a section whose content runs a request (Clinical edits)
// still asks for it only when the clinician opens it, as the old tab did.
import { useId, useState, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { Button } from '@scripsteam/scrips-signal-ds';

export interface PanelSectionProps {
  /** Stable key: `data-section` on the root, and the hook tests and the bridge read. */
  id: string;
  /** Section title (the section role). */
  title: string;
  /** A count or a short meta beside the title (meta role, secondary ink). */
  meta?: ReactNode;
  /** Header actions: `PanelSectionAction`s, drawn right of the toggle. */
  actions?: ReactNode;
  /** Open until the clinician says otherwise. Re-read on every render until the first toggle. */
  defaultOpen?: boolean;
  children?: ReactNode;
}

export function PanelSection({ id, title, meta, actions, defaultOpen = false, children }: PanelSectionProps) {
  // `null` = untouched, so the default can still follow the content (a feed that finishes loading).
  const [chosen, setChosen] = useState<boolean | null>(null);
  const open = chosen ?? defaultOpen;
  const uid = useId();
  const headId = `${uid}-head`;
  const bodyId = `${uid}-body`;

  return (
    <section data-slot="panel-section" data-section={id} data-open={open ? '' : undefined} aria-labelledby={headId}>
      <div data-slot="panel-section-header">
        <h3 data-slot="panel-section-heading">
          <button
            type="button"
            id={headId}
            data-slot="panel-section-toggle"
            aria-expanded={open}
            aria-controls={open ? bodyId : undefined}
            onClick={() => setChosen(!open)}
          >
            <ChevronRight aria-hidden data-slot="panel-section-chevron" />
            <span data-slot="panel-section-title">{title}</span>
            {meta != null && meta !== '' ? <span data-slot="panel-section-meta">{meta}</span> : null}
          </button>
        </h3>
        {actions ? <div data-slot="panel-section-actions">{actions}</div> : null}
      </div>
      {open ? (
        <div id={bodyId} data-slot="panel-section-body">
          {children}
        </div>
      ) : null}
    </section>
  );
}

export interface PanelSectionActionProps {
  /** The accessible name, which the app-wide instant tooltip shows. */
  label: string;
  /** A Lucide icon element (drawn at the app's 2 px). */
  icon: ReactNode;
  onClick: () => void;
  /** A toggle action reports its state (Hide completed). */
  pressed?: boolean;
}

/** A 32 px neutral icon button in a section header: the DS tertiary Button with a Lucide glyph. */
export function PanelSectionAction({ label, icon, onClick, pressed }: PanelSectionActionProps) {
  return (
    <Button
      variant="tertiary"
      size="sm"
      aria-label={label}
      aria-pressed={pressed}
      data-slot="panel-section-action"
      icon={icon}
      onClick={onClick}
    />
  );
}
